import { ref, reactive } from 'vue';
import { StorageService } from '@/services/StorageService.js';

/**
 * Composable for settings management
 * Handles settings state, persistence, and team member operations
 */
export function useSettings() {
    // Settings state
    const settings = reactive(StorageService.loadSettings());
    const showSettings = ref(false);

    /**
     * Save current settings to localStorage
     */
    const saveSettings = () => {
        StorageService.saveSettings(settings);
        // Don't close modal here - let App.vue handle it
    };

    /**
     * Add a new team member
     */
    const addMember = () => {
        const newMemberNumber = settings.teamMembers.length + 1;
        settings.teamMembers.push(`Membro ${newMemberNumber}`);
    };

    /**
     * Remove a team member by index
     * @param {number} index - Index of member to remove
     */
    const removeMember = (index) => {
        if (settings.teamMembers.length > 1) {
            settings.teamMembers.splice(index, 1);
        }
    };

    /**
     * Toggle settings modal visibility
     */
    const toggleSettings = () => {
        showSettings.value = !showSettings.value;
    };

    /**
     * Close settings modal
     */
    const closeSettings = () => {
        showSettings.value = false;
    };

    /**
     * Reset settings to defaults
     */
    const resetSettings = () => {
        Object.assign(settings, StorageService.DEFAULT_SETTINGS);
    };

    /**
     * Validate settings values
     * @returns {Object} Validation result with isValid flag and errors
     */
    const validateSettings = () => {
        const errors = [];
        
        if (settings.deadlineDuration < 1 || settings.deadlineDuration > 30) {
            errors.push('Deadline duration must be between 1 and 30 minutes');
        }
        
        if (settings.projectDuration < 1 || settings.projectDuration > 60) {
            errors.push('Project duration must be between 1 and 60 minutes');
        }
        
        if (settings.personalDuration < 1 || settings.personalDuration > 10) {
            errors.push('Personal duration must be between 1 and 10 minutes');
        }
        
        if (settings.teamMembers.length === 0) {
            errors.push('At least one team member is required');
        }
        
        // Check for empty team member names
        const emptyMembers = settings.teamMembers.filter(member => !member.trim());
        if (emptyMembers.length > 0) {
            errors.push('Team member names cannot be empty');
        }
        
        return {
            isValid: errors.length === 0,
            errors
        };
    };

    return {
        // State
        settings,
        showSettings,
        
        // Methods
        saveSettings,
        addMember,
        removeMember,
        toggleSettings,
        closeSettings,
        resetSettings,
        validateSettings
    };
}